import { motion } from "framer-motion";
import { Scale, Gavel, FileCheck, Mail } from "lucide-react";
import { PageHeader } from "@/components/layout/Navigation";

const sections = [
  {
    id: "acceptance",
    title: "1. Acceptance of Terms",
    body: [
      "These Terms of Service (\"Terms\") form a binding agreement between NeoBody Inc. (\"NeoBody,\" \"we,\" \"us,\" or \"our\") and the entity or individual (\"Customer,\" \"you,\" or \"your\") that accesses or uses our software, websites, or related services (collectively, the \"Services\").",
      "By signing an order form, clicking \"I agree,\" creating an account, or otherwise using the Services, you agree to these Terms. If you are entering into these Terms on behalf of an organization, you represent that you have the authority to bind that organization."
    ]
  },
  {
    id: "services",
    title: "2. The Services",
    body: [
      "NeoBody provides cloud-based client management software for healthcare organizations, including modules for scheduling, documentation, communications, billing support, and analytics. Specific features, usage limits, support levels, and fees are described in the applicable order form or subscription plan.",
      "We may modify, enhance, or discontinue features over time. We will not materially decrease the core functionality of a paid subscription during its current term."
    ]
  },
  {
    id: "accounts",
    title: "3. Accounts and Authorized Users",
    body: [
      "You are responsible for maintaining the confidentiality of account credentials and for all activity under your account. You agree to notify us promptly of any unauthorized access. Each authorized user must be assigned a unique login—shared logins are prohibited.",
      "You will ensure that all authorized users comply with these Terms and use the Services only in accordance with applicable laws and any relevant Business Associate Agreement."
    ]
  },
  {
    id: "customer-data",
    title: "4. Customer Data and HIPAA",
    body: [
      "As between the parties, you own all data that you or your authorized users submit to the Services (\"Customer Data\"). You grant NeoBody a limited license to host, copy, transmit, and display Customer Data solely as needed to provide the Services.",
      "Where the Services are used to process Protected Health Information (PHI), the parties will execute a Business Associate Agreement (BAA), which governs NeoBody's use and disclosure of PHI and is incorporated into these Terms by reference.",
      "You are responsible for the accuracy, quality, legality, and appropriate use of Customer Data and for obtaining all consents required to provide it to NeoBody."
    ]
  },
  {
    id: "acceptable-use",
    title: "5. Acceptable Use",
    body: [
      "You agree not to: (a) use the Services in violation of law or third-party rights; (b) attempt to gain unauthorized access to the Services or related systems; (c) reverse engineer, decompile, or copy the Services except as expressly permitted; (d) interfere with the integrity or performance of the Services or the data they contain; (e) upload viruses, malware, or other harmful code; or (f) use the Services to send unsolicited communications in violation of applicable law.",
      "We may suspend access to investigate suspected violations or to protect the security or availability of the Services."
    ]
  },
  {
    id: "fees",
    title: "6. Fees and Payment",
    body: [
      "Fees are described in your order form or subscription plan. Unless otherwise stated, fees are billed in advance, non-refundable, and exclusive of taxes. Late payments accrue interest at the lesser of 1.5% per month or the maximum rate permitted by law.",
      "If we change pricing for a subscription renewal, we will provide notice at least 30 days before the renewal date. You may decline the renewal by notifying us before the end of the then-current term."
    ]
  },
  {
    id: "term-termination",
    title: "7. Term and Termination",
    body: [
      "These Terms remain in effect for the subscription term set out in the order form and renew automatically for successive periods of equal length unless either party gives notice of non-renewal at least 30 days before the end of the current term.",
      "Either party may terminate these Terms for material breach if the breach is not cured within 30 days after written notice. We may suspend or terminate the Services immediately for non-payment, security risk, or violation of Section 5 (Acceptable Use).",
      "Upon termination, you may export Customer Data for 30 days, after which we will delete it in accordance with our standard procedures and the BAA."
    ]
  },
  {
    id: "ip",
    title: "8. Intellectual Property",
    body: [
      "NeoBody and its licensors retain all right, title, and interest in and to the Services, including all software, content, trademarks, logos, and underlying technology. No rights are granted to you except as expressly set out in these Terms.",
      "If you provide feedback or suggestions, you grant NeoBody a perpetual, royalty-free license to use that feedback to improve the Services without obligation to you."
    ]
  },
  {
    id: "confidentiality",
    title: "9. Confidentiality",
    body: [
      "Each party agrees to protect the other party's Confidential Information using at least the same degree of care it uses to protect its own confidential information of like importance, and in any event no less than a reasonable standard of care. Confidential Information may be used only to perform under these Terms and disclosed only to personnel and contractors with a need to know who are bound by similar obligations."
    ]
  },
  {
    id: "warranties",
    title: "10. Warranties and Disclaimers",
    body: [
      "NeoBody warrants that the Services will perform materially in accordance with the documentation. As your sole remedy for breach of this warranty, we will use commercially reasonable efforts to correct the non-conformity, or—if we cannot—refund prepaid fees for the affected portion of the Services.",
      "EXCEPT AS EXPRESSLY STATED, THE SERVICES ARE PROVIDED \"AS IS\" AND \"AS AVAILABLE.\" NEOBODY DISCLAIMS ALL OTHER WARRANTIES, EXPRESS, IMPLIED, OR STATUTORY, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. THE SERVICES ARE NOT MEDICAL DEVICES AND DO NOT PROVIDE MEDICAL ADVICE."
    ]
  },
  {
    id: "indemnification",
    title: "11. Indemnification",
    body: [
      "NeoBody will defend you against third-party claims alleging that your authorized use of the Services infringes a U.S. patent, copyright, or trademark, and will pay any damages finally awarded, subject to standard procedural conditions and exclusions for Customer Data, modifications, and combinations.",
      "You will defend NeoBody against third-party claims arising from Customer Data, your violation of law, or your breach of Section 5 (Acceptable Use)."
    ]
  },
  {
    id: "liability",
    title: "12. Limitation of Liability",
    body: [
      "TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEITHER PARTY WILL BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS, REVENUE, OR DATA, EVEN IF ADVISED OF THE POSSIBILITY.",
      "EACH PARTY'S TOTAL LIABILITY ARISING OUT OF OR RELATING TO THESE TERMS WILL NOT EXCEED THE FEES PAID OR PAYABLE BY YOU TO NEOBODY IN THE 12 MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM. THESE LIMITATIONS DO NOT APPLY TO BREACHES OF CONFIDENTIALITY, INDEMNIFICATION OBLIGATIONS, OR LIABILITY THAT CANNOT BE LIMITED UNDER LAW."
    ]
  },
  {
    id: "law",
    title: "13. Governing Law and Disputes",
    body: [
      "These Terms are governed by the laws of the State of New York, without regard to conflict-of-laws principles. The parties consent to exclusive jurisdiction and venue in the state and federal courts located in New York County, New York for any dispute that is not subject to arbitration.",
      "Either party may seek injunctive relief in any court of competent jurisdiction to protect its intellectual property or Confidential Information."
    ]
  },
  {
    id: "changes",
    title: "14. Changes to These Terms",
    body: [
      "We may update these Terms from time to time. If we make material changes, we will notify you by email or through the Services at least 30 days before the changes take effect. Your continued use after the effective date constitutes acceptance."
    ]
  },
  {
    id: "general",
    title: "15. General",
    body: [
      "These Terms, together with any order form, BAA, and our Privacy Policy, constitute the entire agreement between the parties on this subject. If any provision is held unenforceable, the remainder will remain in effect. Failure to enforce a right is not a waiver. Neither party may assign these Terms without the other's consent, except in connection with a merger, acquisition, or sale of substantially all assets. Notices must be in writing and sent to the addresses on the order form or to legal@neobody.com."
    ]
  },
  {
    id: "contact",
    title: "16. Contact",
    body: [
      "Questions about these Terms? Contact our legal team.",
      "NeoBody Inc. — Legal",
      "Email: legal@neobody.com",
      "Phone: (347) 342-8822"
    ]
  }
];

const TermsPage = () => {
  return (
    <div data-testid="terms-page">
      <PageHeader
        title="Terms of Service"
        subtitle="The agreement that governs your use of NeoBody."
        breadcrumbs={[{ name: "Terms of Service" }]}
      />

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          {/* Intro cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16"
          >
            <div className="bg-[#F9F9F7] rounded-2xl p-6 border border-[#1B4D3E]/10" data-testid="terms-card-fair">
              <div className="w-12 h-12 rounded-xl bg-[#1B4D3E]/5 flex items-center justify-center mb-4">
                <Scale className="w-6 h-6 text-[#1B4D3E]" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-xl text-[#1A1A1A] mb-2">Plain & Fair</h3>
              <p className="text-sm text-[#4A4A4A] leading-relaxed">
                Written in clear language, with reasonable terms for both sides.
              </p>
            </div>
            <div className="bg-[#F9F9F7] rounded-2xl p-6 border border-[#1B4D3E]/10" data-testid="terms-card-baa">
              <div className="w-12 h-12 rounded-xl bg-[#1B4D3E]/5 flex items-center justify-center mb-4">
                <FileCheck className="w-6 h-6 text-[#1B4D3E]" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-xl text-[#1A1A1A] mb-2">BAA Included</h3>
              <p className="text-sm text-[#4A4A4A] leading-relaxed">
                Every covered entity customer signs a Business Associate Agreement with us.
              </p>
            </div>
            <div className="bg-[#F9F9F7] rounded-2xl p-6 border border-[#1B4D3E]/10" data-testid="terms-card-data">
              <div className="w-12 h-12 rounded-xl bg-[#1B4D3E]/5 flex items-center justify-center mb-4">
                <Gavel className="w-6 h-6 text-[#1B4D3E]" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-xl text-[#1A1A1A] mb-2">You Own Your Data</h3>
              <p className="text-sm text-[#4A4A4A] leading-relaxed">
                Customer Data belongs to you. We host and process it under your direction.
              </p>
            </div>
          </motion.div>

          {/* Last updated */}
          <p className="text-sm text-[#888] mb-12" data-testid="terms-last-updated">
            Last updated: February 1, 2026
          </p>

          {/* Table of contents */}
          <div className="bg-[#F9F9F7] rounded-2xl p-8 mb-16 border border-[#1B4D3E]/10">
            <h3 className="font-display text-xl text-[#1A1A1A] mb-4">Contents</h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
              {sections.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="text-[#1B4D3E] hover:text-[#D4AF37] transition-colors text-sm">
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Sections */}
          <div className="space-y-14">
            {sections.map((s, idx) => (
              <motion.div
                key={s.id}
                id={s.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: idx * 0.02 }}
                data-testid={`terms-section-${s.id}`}
              >
                <h2 className="font-display text-2xl md:text-3xl text-[#1A1A1A] mb-4 scroll-mt-32">
                  {s.title}
                </h2>
                <div className="space-y-4">
                  {s.body.map((p, i) => (
                    <p key={i} className="text-[#4A4A4A] font-light leading-relaxed">
                      {p}
                    </p>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Contact CTA */}
          <div className="mt-20 bg-[#1B4D3E] rounded-3xl p-10 md:p-14 text-center">
            <Mail className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-display text-3xl text-white mb-3">Need to talk to legal?</h3>
            <p className="text-white/70 font-light mb-6 max-w-xl mx-auto">
              For procurement, BAA, or contract questions, reach out and we'll route you to the right team.
            </p>
            <a
              href="mailto:legal@neobody.com"
              className="btn-gold inline-flex"
              data-testid="terms-contact-email"
            >
              legal@neobody.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;
